import type {
	AiState,
	IPuSagaRow,
	ISagaRow,
	ISagaStep,
	ISagaView,
	SagaCurrentState,
	SagaLevel,
	SagaVerdict,
	StepKey,
} from '../../../interface/saga-status';

/**
 * Pure derivation of what an agent is told about their submission.
 *
 * No React, no network. Every branch in the agent-facing pipeline lives here so it can be
 * tested exhaustively — the combinations matter more than the rendering.
 */

/** Which stages exist at each level. The iREV branch exists only at polling unit. */
export const STEPS_BY_LEVEL: Record<SagaLevel, StepKey[]> = {
	pu: ['saved', 'confirmed', 'ai', 'irev'],
	ward: ['saved', 'confirmed', 'ai'],
	lga: ['saved', 'confirmed', 'ai'],
	state: ['saved', 'confirmed', 'ai'],
};

const isPuRow = (row: ISagaRow): row is IPuSagaRow => 'isIrevExtractionCompleted' in row;

/**
 * Resolve the two independent AI booleans into one state.
 *
 * The both-true case is the one that gets rendered wrongly: a failure was recorded and the
 * extraction later completed. Data was produced, so this is a warning, not a failure —
 * showing red here sends agents out to re-photograph a sheet that read fine, and once agents
 * learn the alert is noise they stop reading the real ones.
 */
export const aiState = (row: Pick<ISagaRow, 'isAiExtractionCompleted' | 'isAiExtractionFailed'>): AiState => {
	if (row.isAiExtractionCompleted && row.isAiExtractionFailed) return 'warn';
	if (row.isAiExtractionCompleted) return 'done';
	if (row.isAiExtractionFailed) return 'failed';
	return 'pending';
};

/** Terminal for the state machine — nothing further will happen. */
export const isSagaTerminal = (row: ISagaRow | null): boolean =>
	!!row &&
	(['Completed', 'CompletedWithWarnings', 'PermanentlyFailed', 'Final'] as SagaCurrentState[]).includes(row.currentState);

/**
 * Terminal *for the agent* — the screen has told them everything it usefully can.
 *
 * Deliberately different from `isSagaTerminal`. A polling-unit saga in `AwaitingIrev` is not
 * terminal for the machine (server-side polling can run to 60 attempts, potentially an hour)
 * but there is nothing more for the agent to see or do. Conflating the two would either poll
 * for an hour or stop while the result was still unsaved.
 */
export const isAgentTerminal = (view: ISagaView): boolean =>
	(['stalled', 'reviewFigures', 'checkPhoto', 'landed'] as SagaVerdict[]).includes(view.verdict);

/**
 * Whether a manual AI re-extraction would actually do anything.
 *
 * Mirrors the server's own correlation guard: the state machine accepts the replay message
 * only in these states and silently drops it everywhere else — while the endpoint returns 202
 * regardless. Without this check the UI would offer a button that reports success and does
 * nothing, which is worse than offering no button.
 */
export const canTriggerAi = (row: ISagaRow | null): boolean =>
	!!row &&
	(['Processing', 'AwaitingIrev', 'Completed', 'CompletedWithWarnings'] as SagaCurrentState[]).includes(row.currentState) &&
	(aiState(row) === 'failed' || row.mismatch === 'AiValidationFailed');

/** Read the entity code off a row whichever level it came from. */
export const rowCode = (row: ISagaRow | null): string => {
	if (!row) return '';
	const anyRow = row as unknown as Record<string, unknown>;
	for (const key of ['pollingUnitCode', 'wardCode', 'lgaCode', 'stateCode']) {
		const value = anyRow[key];
		if (typeof value === 'string' && value.trim()) return value;
	}
	return '';
};

const STEP_LABELS: Record<StepKey, string> = {
	saved: 'Saved',
	confirmed: 'Confirmed on your assignment',
	ai: 'Checking your photo',
	irev: 'Comparing with INEC IReV',
};

const buildSteps = (row: ISagaRow, level: SagaLevel): ISagaStep[] => {
	const terminal = isSagaTerminal(row);
	const ai = aiState(row);

	// A stage that hasn't happened reads as "pending" only while something can still happen.
	// Once the saga is permanently failed, calling it pending is a lie.
	const waiting = (done: boolean): ISagaStep['state'] =>
		done ? 'done' : row.currentState === 'PermanentlyFailed' ? 'failed' : 'active';

	const byKey: Record<StepKey, ISagaStep> = {
		saved: {
			key: 'saved',
			label: STEP_LABELS.saved,
			state: waiting(row.isSqlSyncCompleted),
			note: row.isSqlSyncCompleted ? '' : 'Storing your result.',
		},
		confirmed: {
			key: 'confirmed',
			label: STEP_LABELS.confirmed,
			state: waiting(row.isAgentCallbackCompleted),
			note: '',
		},
		ai: {
			key: 'ai',
			label: STEP_LABELS.ai,
			state: ai === 'pending' ? (terminal ? 'pending' : 'active') : ai,
			note:
				ai === 'failed' ? 'We couldn’t read the sheet clearly.' : ai === 'pending' && !terminal ? 'Usually under a minute.' : '',
		},
		irev: {
			key: 'irev',
			label: STEP_LABELS.irev,
			state: isPuRow(row) ? (row.isIrevExtractionCompleted ? 'done' : terminal ? 'pending' : 'active') : 'pending',
			// Never surface irevPollCount — it is a duration proxy, and "23/60" reads to an agent
			// as a countdown to failure over something they cannot influence.
			note: isPuRow(row) && !row.isIrevExtractionCompleted ? 'INEC hasn’t published this unit yet. Nothing for you to do.' : '',
		},
	};

	return STEPS_BY_LEVEL[level].map((key) => byKey[key]);
};

/**
 * Plain-language copy for the pipeline's validation verdict.
 *
 * The iREV wording is deliberate and must not be softened into agent error. An iREV mismatch
 * is a signal *about INEC's published figures*, not an accusation against the agent — telling
 * an agent they are wrong would push them to "correct" their figures to match INEC, which
 * destroys the entire evidentiary value of an independent parallel count.
 */
export const MISMATCH_COPY: Record<'AiValidationFailed' | 'IrevValidationFailed', { headline: string; detail: string }> = {
	AiValidationFailed: {
		headline: 'Check your figures',
		detail:
			'We read the sheet you uploaded and got different numbers from the ones you typed. Your result is saved either way — open it to compare.',
	},
	IrevValidationFailed: {
		headline: 'Your figures differ from INEC’s copy',
		detail:
			'INEC has published a result for this unit that doesn’t match yours. This doesn’t mean you’re wrong — it’s exactly the kind of difference iElect exists to catch. Your submission stands.',
	},
};

const UNKNOWN_VIEW: ISagaView = {
	verdict: 'unknown',
	headline: 'We can’t check the status right now',
	detail: 'Your result was sent. We’ll keep trying to confirm what happened to it.',
	steps: [],
	isLanded: false,
};

/**
 * Turn a saga row into what the agent sees.
 *
 * The load-bearing decision is the definition of "landed": the agent's obligation is
 * discharged once the result is stored and confirmed against their assignment. AI extraction
 * and the iREV comparison are downstream checks they cannot influence and often wait a long
 * time on, so a pending stage must not hold the headline hostage — otherwise the common,
 * healthy case reads as an unfinished, worrying form.
 */
export const deriveSagaView = (row: ISagaRow | null, level: SagaLevel): ISagaView => {
	if (!row) return UNKNOWN_VIEW;

	const steps = buildSteps(row, level);
	const landed = row.isSqlSyncCompleted && row.isAgentCallbackCompleted;
	const base = { steps, isLanded: landed };

	if (row.currentState === 'PermanentlyFailed') {
		return {
			...base,
			verdict: 'stalled',
			headline: 'We couldn’t finish saving this',
			detail: 'Your result didn’t complete. Upload it again to try afresh.',
			action: { label: 'Upload again', kind: 'reupload' },
		};
	}

	if (row.mismatch !== 'None') {
		const copy = MISMATCH_COPY[row.mismatch];
		return {
			...base,
			verdict: 'reviewFigures',
			headline: copy.headline,
			detail: copy.detail,
			action: { label: 'View your result', kind: 'compare' },
		};
	}

	if (aiState(row) === 'failed') {
		return {
			...base,
			verdict: 'checkPhoto',
			headline: 'Check your photo',
			detail: 'Your result is saved. But we couldn’t read the sheet photo clearly enough to check it.',
			action: { label: 'Take a clearer photo', kind: 'reupload' },
		};
	}

	if (landed) {
		return {
			...base,
			verdict: 'landed',
			headline: 'Your result is in',
			detail: 'Saved and confirmed against your assignment.',
		};
	}

	return {
		...base,
		verdict: 'working',
		headline: 'We’re saving your result',
		detail: 'This usually takes a few seconds. You can close this screen — it will keep going.',
	};
};

/**
 * Polling cadence, in milliseconds, by attempt number.
 *
 * Fast while the agent is watching, then backing off. Jitter is applied by the caller: a
 * polling-unit hall where a dozen agents submit at the same moment must not synchronise every
 * device onto the same tick.
 */
export const nextPollDelay = (attempt: number): number => {
	if (attempt <= 0) return 2_000;
	if (attempt <= 6) return 5_000;
	if (attempt <= 8) return 10_000;
	if (attempt <= 10) return 20_000;
	return 30_000;
};

/** ±20% jitter. `random` is injected so tests stay deterministic. */
export const withJitter = (delay: number, random: () => number = Math.random): number =>
	Math.round(delay * (0.8 + random() * 0.4));

/** Stop polling entirely after this long, however the saga looks. */
export const POLL_CEILING_MS = 10 * 60 * 1000;
